import ModalType from '@/Enums/Pixels/ModalType.enum';
import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import type { ISegmentControlOption } from '@/Interfaces/ControlOption.interface';
import type { IParsedEvent } from '@/Interfaces/Pixels/Event.interface';
import type { IPixelFormData } from '@/Interfaces/Pixels/Pixel.interface';
import CodeSection from '@/components/CodeSection';
import ModalHeaderIcon from '@/components/Icons/ModalHeaderIcon';
import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import ModalBody from '@/components/Modal/common/ModalBody';
import ModalTitleCard from '@/components/Modal/common/ModalTitleCard';
import NumberInput from '@/components/NumberInput';
import { DEFAULT_PIXEL, MODAL_TITLES, NEW_SEGMENT_OPTION, SUBMIT_BUTTON_TITLES } from '@/constants/pixels';
import { createNewSegmentControlOption } from '@/helpers/pixels';
import { createPixel, createSegment, updatePixel } from '@/store/api/pixels.api';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectMeScreenAccess } from '@/store/selectors/me.selectors';
import { selectPixelsAdvertisers, selectSelectedPixel } from '@/store/selectors/pixel.selectors';
import { addNewSegment } from '@/store/slices/pixelsSlice';
import { Autocomplete, Button, InputLabel, Modal, TextField } from '@mui/material';
import { HttpStatusCode } from 'axios';
import { type SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

interface IProps {
  open: boolean;
  handleClose: (pixel?: Partial<IParsedEvent>) => void;
  modalType: ModalType;
}

const PixelModal = ({ open, handleClose, modalType }: IProps) => {
  const dispatch = useAppDispatch();
  const { selectedPixel, selectedPixelTag, selectedPixelTagLoading } = useAppSelector(selectSelectedPixel);

  const advertisers = useAppSelector(selectPixelsAdvertisers);

  const readOnly = useAppSelector(selectMeScreenAccess(ScreenKey.PIXELS));

  const [loading, setLoading] = useState<boolean>(false);

  //#region form
  const formMethods = useForm<IPixelFormData>({
    defaultValues: useMemo(() => {
      if (modalType === ModalType.ViewPixel) {
        return selectedPixel;
      }
      return DEFAULT_PIXEL;
    }, [modalType, selectedPixel]),
  });

  const { reset, handleSubmit, watch, resetField, getValues, setError, clearErrors, setValue, control } = formMethods;

  const selectedAdvertiser = useWatch({ control, name: 'advertiser' });
  const selectedSegment = useWatch({ control, name: 'segment' });

  const segmentLocked = useWatch({ control, name: 'segmentLocked' });

  useEffect(() => {
    if (!watch) return;
    const subscription = watch((_value, { name, type }) => {
      if (name === 'advertiser' && type === 'change') {
        resetField('segment');
        setValue('segmentLocked', false);
      }
      if (name === 'segment' && type === 'change') {
        resetField('newSegment');
      }
    });
    return () => subscription.unsubscribe();
  }, [resetField, setValue, watch]);

  useEffect(() => {
    reset(selectedPixel);
  }, [reset, selectedPixel]);
  //#endregion

  const saveNewSegment = useCallback(
    (newSegment: ISegmentControlOption) => dispatch(addNewSegment(newSegment)),
    [dispatch],
  );

  const onClose = useCallback(() => {
    handleClose();
    reset();
  }, [handleClose, reset]);

  const onModalClose = useCallback(
    (_event: SyntheticEvent, reason: string) => {
      if (reason) return;
      onClose();
    },
    [onClose],
  );

  const onSubmit = useCallback(
    async (data: IPixelFormData) => {
      switch (modalType) {
        case ModalType.NewPixel: {
          setLoading(true);
          try {
            const response = await createPixel(data);
            if (response.status !== HttpStatusCode.Created) {
              throw new Error('Failed to create a Pixel');
            }

            const newPixelAdvertiser = data.advertiser;
            const newPixelSegment = data.segment || {};

            handleClose({
              ...data,
              advertiser: newPixelAdvertiser,
              segment: newPixelSegment,
              id: response.data.id,
            });
            toast.success('Pixel created successfully');
          } catch (error) {
            console.error(error);
            toast.error('Something went wrong');
          } finally {
            setLoading(false);
          }
          return;
        }
        case ModalType.ViewPixel: {
          setLoading(true);
          try {
            await updatePixel(data);

            handleClose(data);
            toast.success('Pixel updated successfully');

            reset();
          } catch (error) {
            console.error(error);
            toast.error('Something went wrong');
          } finally {
            setLoading(false);
          }
          return;
        }
        default:
          return;
      }
    },
    [modalType, reset, handleClose],
  );

  const onSaveSegment = useCallback(async () => {
    clearErrors('newSegment');
    const [advertiser, newSegment] = getValues(['advertiser', 'newSegment']);
    if (!newSegment) {
      setError('newSegment', { message: 'Segment Name is required' });
      return;
    }
    if (advertiser?.segments?.some(segment => segment.label === newSegment)) {
      setError('newSegment', { message: 'Segment Name should be unique per advertiser' });
      return;
    }

    setLoading(true);
    try {
      const responseSegment = await createSegment(newSegment, advertiser?.value);

      const newSegmentControlOption = createNewSegmentControlOption(advertiser?.value, newSegment, responseSegment.id);
      saveNewSegment(newSegmentControlOption);
      setValue('segment', newSegmentControlOption, { shouldValidate: true });
      setValue('advertiser', { ...advertiser, segments: [...advertiser.segments, newSegmentControlOption] });
      // lock segment
      setValue('segmentLocked', true);
      toast.success('Segment created successfully');
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  }, [clearErrors, getValues, setError, setValue, saveNewSegment]);

  const disabled = useMemo(() => modalType === ModalType.ViewPixel, [modalType]);
  const showNewSegment = useMemo(() => selectedSegment?.isCreateNewOption, [selectedSegment?.isCreateNewOption]);

  //#region select options
  const segmentOptions = useMemo(() => {
    const options = [NEW_SEGMENT_OPTION];

    if (selectedAdvertiser?.segments?.length) {
      options.push(...selectedAdvertiser.segments);
    }

    return options;
  }, [selectedAdvertiser]);
  //#endregion

  return (
    <LoadingBackdropWrapper backdropOpen={loading}>
      <Modal open={open} onClose={onModalClose}>
        <ModalBody>
          <ModalTitleCard titleIcon={<ModalHeaderIcon />} handleClose={onClose} title={MODAL_TITLES[modalType]} />
          <FormProvider {...formMethods}>
            <form className='mt-4' onSubmit={handleSubmit(onSubmit)}>
              <InputLabel sx={{ marginBottom: '4px' }} htmlFor='pixel-name'>
                Pixel Name
              </InputLabel>
              <Controller
                name='name'
                control={control}
                disabled={readOnly}
                rules={{ required: { message: 'Pixel Name is required', value: true } }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    placeholder='Enter Pixel Name'
                    id='pixel-name'
                    sx={{ marginBottom: '8px' }}
                    slotProps={{ htmlInput: { maxLength: 99 } }}
                  />
                )}
              />
              <InputLabel sx={{ marginBottom: '4px' }} htmlFor='advertiser-select'>
                Advertiser
              </InputLabel>
              <Controller
                name='advertiser'
                control={control}
                rules={{ required: { message: 'Advertiser is required', value: true } }}
                disabled={readOnly}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    id='advertiser-select'
                    sx={{ marginBottom: '8px' }}
                    options={advertisers}
                    fullWidth
                    disabled={disabled}
                    onChange={(_, data) => field.onChange(data)}
                    getOptionLabel={option => option?.label || ''}
                    getOptionKey={option => option.value}
                    disableClearable
                    isOptionEqualToValue={(option, selectValue) => selectValue && option.value === selectValue?.value}
                    renderInput={params => (
                      <TextField
                        {...params}
                        error={!!error}
                        helperText={error?.message}
                        placeholder='Advertiser'
                        variant='outlined'
                      />
                    )}
                  />
                )}
              />
              <InputLabel sx={{ marginBottom: '4px' }} htmlFor='segment-select'>
                <span>Segment</span>{' '}
                <span className='ml-[16px]'>
                  *(If intended to be used for retargeting, please select or create segment)
                </span>
              </InputLabel>
              <Controller
                name='segment'
                control={control}
                rules={{
                  validate: value => {
                    if (value?.isCreateNewOption) {
                      return 'Please finish creating a new segment first';
                    }
                    return true;
                  },
                }}
                disabled={readOnly}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    id='segment-select'
                    sx={{ marginBottom: '8px' }}
                    options={segmentOptions}
                    fullWidth
                    disabled={disabled || !selectedAdvertiser || segmentLocked}
                    onChange={(_, data) => field.onChange(data)}
                    getOptionLabel={option => option?.label || ''}
                    disableClearable
                    isOptionEqualToValue={(option, selectValue) => selectValue && option.value === selectValue?.value}
                    renderInput={params => (
                      <TextField
                        {...params}
                        error={!!error}
                        helperText={error?.message}
                        placeholder={disabled ? '' : 'Segment'}
                        variant='outlined'
                      />
                    )}
                  />
                )}
              />
              {showNewSegment ? (
                <>
                  <InputLabel sx={{ marginBottom: '4px' }} htmlFor='new-segment-select'>
                    Segment Name
                  </InputLabel>
                  <div className='flex items-start justify-between mb-[8px] gap-x-[8px]'>
                    <Controller
                      name='newSegment'
                      rules={{
                        required: {
                          message: 'Segment Name is required',
                          value: true,
                        },
                      }}
                      disabled={readOnly}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          id='new-segment-select'
                          fullWidth
                          error={!!error}
                          helperText={error?.message}
                          placeholder={disabled ? '' : 'Segment'}
                          variant='outlined'
                          slotProps={{ htmlInput: { maxLength: 190 } }}
                        />
                      )}
                    />
                    <Button
                      sx={{ height: '47px', width: '85px' }}
                      variant='contained'
                      type='button'
                      onClick={onSaveSegment}
                    >
                      Save
                    </Button>
                  </div>
                </>
              ) : null}
              <div className='flex gap-x-[12px]'>
                <div className='flex-1'>
                  <InputLabel sx={{ marginBottom: '4px' }} htmlFor='click-window'>
                    Click Window (days)
                  </InputLabel>
                  <Controller
                    name='clickWindow'
                    control={control}
                    rules={{
                      required: {
                        message: 'Click Window is required',
                        value: true,
                      },
                    }}
                    disabled={readOnly}
                    render={({ field, fieldState }) => (
                      <NumberInput
                        {...field}
                        //@ts-expect-error js component
                        fullWidth
                        min={1}
                        max={30}
                        maximumFractionDigits={0}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        placeholder='Enter Click Window'
                        id='click-window'
                        sx={{ marginBottom: '8px' }}
                      />
                    )}
                  />
                </div>
                <div className='flex-1'>
                  <InputLabel sx={{ marginBottom: '4px' }} htmlFor='view-window'>
                    View Window (days)
                  </InputLabel>
                  <Controller
                    name='viewWindow'
                    control={control}
                    rules={{ required: { message: 'View Window is required', value: true } }}
                    disabled={readOnly}
                    render={({ field, fieldState }) => (
                      <NumberInput
                        {...field}
                        //@ts-expect-error js component
                        fullWidth
                        min={1}
                        max={30}
                        maximumFractionDigits={0}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        placeholder='Enter View Window'
                        id='view-window'
                        sx={{ marginBottom: '8px' }}
                      />
                    )}
                  />
                </div>
              </div>
              <Button variant='contained' sx={{ marginTop: '8px' }} type='submit' disabled={readOnly}>
                {SUBMIT_BUTTON_TITLES[modalType]}
              </Button>
            </form>
            {modalType !== ModalType.NewPixel ? (
              <CodeSection label='Generated Tag' content={selectedPixelTag} loading={!!selectedPixelTagLoading} />
            ) : null}
            <hr className='-mx-[32px] my-5' />
            <div className='mt-5 flex justify-end'>
              <Button color='inherit' variant='outlined' onClick={onClose}>
                Close
              </Button>
            </div>
          </FormProvider>
        </ModalBody>
      </Modal>
    </LoadingBackdropWrapper>
  );
};

export default PixelModal;
